import { Button, calendarDay, zonedInstant } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Calendar } from "@majitap/ui/arc/calendar/calendar";
import { Checkbox } from "@majitap/ui/arc/checkbox/checkbox";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { TimePicker } from "@majitap/ui/arc/time-picker/time-picker";
import { useMutation } from "@tanstack/react-query";
import { Info } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { dispatchActions } from "../../dispatch";
import { messageFor } from "../../error-copy";
import { useRefreshBoard } from "./actions";
import styles from "./assign-dialog.module.css";

/** "09:30" of an instant in the company's zone. */
const clock = (iso: string, timezone: string) =>
  new Intl.DateTimeFormat("en-GB", { timeZone: timezone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(
    new Date(iso),
  );

/**
 * Move a delivery that hasn't left to another time (P8-75, `Console / Dispatch / Table /
 * Reschedule`): a future day and time, earlier or later, with a reason for its timeline. An
 * assigned driver is released and the fill site kept; the customer can be texted the new date.
 */
export function RescheduleDialog({
  delivery,
  onDone,
  onClose,
}: {
  delivery: {
    id: string;
    delivery_number: string;
    scheduled_at: string | null;
    customer: string;
    fillsite: string | null;
    driver: string | null;
  };
  /** after it moved */
  onDone: () => void;
  onClose: () => void;
}): ReactNode {
  const format = useCompanyFormat();
  const refresh = useRefreshBoard();
  const today = calendarDay(new Date().toISOString(), format.timezone);
  const [day, setDay] = useState<Date | undefined>(
    delivery.scheduled_at ? calendarDay(delivery.scheduled_at, format.timezone) : undefined,
  );
  const [time, setTime] = useState(delivery.scheduled_at ? clock(delivery.scheduled_at, format.timezone) : "09:00");
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [tried, setTried] = useState(false);
  const at = day ? zonedInstant(day, time, format.timezone) : null;
  const inPast = at !== null && Date.parse(at) <= Date.now();

  const move = useMutation({
    mutationFn: () =>
      dispatchActions.reschedule(delivery.id, {
        scheduled_at: at ?? "",
        reason: reason.trim(),
        notify_customer: notify,
      }),
    onSuccess: async () => {
      await refresh();
      onDone();
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    if (at && !inPast && reason.trim()) move.mutate();
  };
  const now = delivery.scheduled_at
    ? new Intl.DateTimeFormat("en-GB", {
        timeZone: format.timezone,
        weekday: "short",
        day: "numeric",
        month: "short",
      }).format(new Date(delivery.scheduled_at)) + `, ${clock(delivery.scheduled_at, format.timezone)}`
    : "as soon as possible";
  const label = at
    ? `${new Intl.DateTimeFormat("en-GB", { timeZone: format.timezone, weekday: "short", day: "numeric", month: "short" }).format(new Date(at))}, ${time}`
    : null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={`Reschedule ${delivery.delivery_number}`} description={`${delivery.customer} · now ${now}`}>
        <form onSubmit={submit} className={styles.form}>
          <div className={styles.calendar}>
            <Calendar value={day} onChange={setDay} minDate={today} showToday />
          </div>
          <div className={styles.row}>
            <TimePicker label="Time" value={time} onChange={setTime} format="24h" minuteStep={15} />
          </div>
          {tried && !at ? <p className={styles.error}>Pick a day.</p> : null}
          {inPast ? <p className={styles.error}>Pick a time in the future.</p> : null}
          <Input
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="The customer asked for a later date"
            description="It goes on the delivery's timeline."
            error={tried && !reason.trim() ? "Say why it moves." : undefined}
          />
          <Checkbox
            label="Text the customer the new date"
            checked={notify}
            onCheckedChange={(v) => setNotify(v === true)}
          />
          {delivery.driver ? (
            <p className={styles.notice}>
              <Info size={14} aria-hidden />
              {`${delivery.driver} is released for other work.${delivery.fillsite ? ` ${delivery.fillsite} stays.` : ""} Plan a driver for the new day.`}
            </p>
          ) : null}
          {move.error ? (
            <Alert tone="danger" title="Not rescheduled">
              {messageFor(errorOf(move.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={move.isPending}>
              {label && !inPast ? `Move to ${label}` : "Reschedule"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
